import { NavLink, Outlet, Navigate, useLocation, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { getMe } from '../api/endpoints';
import { Spinner } from './Spinner';
import { USER_AUTH_ENABLED, isActiveRole } from '../features/users/roles';
import { msalEnabled, logout } from '../auth/msal';

// 操作マニュアル（Google Sites）
const MANUAL_URL = 'https://sites.google.com/sankovalve.com/uzumeai';

const navItems = [
  { to: '/', label: 'アップロード' },
  { to: '/ocr', label: 'OCR' },
  { to: '/orders', label: 'ERP書込み' },
  { to: '/master', label: 'マスタ管理', activeUserOnly: true },
  { to: '/history', label: '選択履歴', devOnly: true },
  { to: '/memory', label: 'メモリ', devOnly: true },
  { to: '/search', label: '検索・評価', devOnly: true },
  { to: '/users', label: 'ユーザー管理', adminOnly: true },
  { to: MANUAL_URL, label: 'マニュアル', external: true },
];

const DEV = import.meta.env.DEV;
// admin 限定パス（URL直打ちを弾く）。マスタ管理は一般ユーザーにも開放したため含めない。
const ADMIN_PATHS = ['/users'];

export function Layout() {
  const location = useLocation();
  // 認証無効（MVP）では getMe を呼ばず従来挙動
  const me = useApi(() => (USER_AUTH_ENABLED ? getMe() : Promise.resolve(null)), []);

  if (USER_AUTH_ENABLED && me.loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const meData = me.data;
  const role = meData?.user?.role;
  const enabled = meData?.user?.enabled ?? true;
  const registered = !!meData?.registered;
  const isAdmin = USER_AUTH_ENABLED && registered && role === 'admin' && enabled;
  // getMe 成功時のみゲート（API障害時は締め出さない）
  const authResolved = USER_AUTH_ENABLED && !me.error && meData != null;
  const needsRegistration = authResolved && (!registered || !isActiveRole(role) || !enabled);
  const onRegister = location.pathname === '/register';

  const items = navItems.filter((it) => {
    if (it.external) return true;
    if (it.adminOnly) return USER_AUTH_ENABLED ? isAdmin : DEV;
    // 一般開放（admin/general）。認証有効時は登録ユーザー全員に表示、MVPはDEVのみ。
    // pending/未登録はそもそも needsRegistration でナビ自体が非表示になる。
    if (it.activeUserOnly) return USER_AUTH_ENABLED || DEV;
    if (it.devOnly) return DEV;
    return true;
  });

  const body = needsRegistration && !onRegister ? (
    <Navigate to="/register" replace />
  ) : !needsRegistration && onRegister ? (
    <Navigate to="/" replace />
  ) : USER_AUTH_ENABLED && !isAdmin && ADMIN_PATHS.some((p) => location.pathname.startsWith(p)) ? (
    <Navigate to="/" replace />
  ) : (
    <Outlet />
  );

  return (
    <div className="h-screen flex flex-col">
      <header className="bg-white border-b-2 border-brand px-6 py-1.5">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/logo-mark.png" alt="三興バルブ HOLDINGS" className="h-7 w-7" />
              <h1 className="text-base font-bold leading-tight">UZUME_AI</h1>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {!needsRegistration && (
              <nav className="flex gap-1">
                {items.map((item) => (item.external ? (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 rounded text-sm text-slate-600 hover:bg-slate-100"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `px-2.5 py-1 rounded text-sm ${
                        isActive ? 'bg-primary text-white' : 'text-slate-600 hover:bg-slate-100'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                )))}
              </nav>
            )}
            {msalEnabled() && (
              <div className="flex items-center gap-2 pl-3 border-l border-border">
                {meData && (
                  <span className="text-xs text-muted">
                    {meData.user?.display_name || meData.email}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => logout()}
                  className="px-2.5 py-1 rounded text-sm text-slate-600 hover:bg-slate-100"
                >
                  ログアウト
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 min-h-0 overflow-auto">
        <div className="px-4 py-3">{body}</div>
      </main>
    </div>
  );
}
